import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { ApiErrorState } from '@/components/ApiErrorState';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { OfferView, PromotionView } from '@/api/generated';
import { formatCivilOrDash } from '@/i18n/format';
import { enumLabel } from '@/i18n/enumLabel';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';
import { StatementsTable } from './StatementsTable';
import { promotionSummary } from './appliedPromotion';
import { SubscriberActions } from './SubscriberActions';
import { SubscriberSeats } from './SubscriberSeats';
import { offerById, offerLabel, offerPeriodLabel } from './offerLabel';
import { StatusChip } from '@/components/status/StatusChip';
import { deriveStatus } from './subscriberStatus';
import type { SubscriberState } from './useSubscriber';

export interface SubscriberDetailProps {
  state: SubscriberState;
  /** The offers the change-offer picker may list (`offers`). */
  offers: OfferView[];
  promotions: PromotionView[];
  /** Set when offers failed to load; the change-offer dialog shows it. */
  offersLoad?: { error: unknown; reload: () => void } | null;
  /** Reloads the offers after change-offer finds one closed. */
  onRefreshOffers?: (() => void) | undefined;
  /** Set when promotions failed to load; the apply-promotion dialog shows it. */
  promotionsLoad?: { error: unknown; reload: () => void } | null;
  currency: string;
  onClose: () => void;
  onDone: (opts?: { quiet?: boolean }) => void;
}

export function SubscriberDetail({
  state,
  offers,
  promotions,
  offersLoad = null,
  onRefreshOffers,
  promotionsLoad = null,
  currency,
  onClose,
  onDone,
}: SubscriberDetailProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const unavailable = t('subscribers.unavailable');

  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <Box role="status" aria-label={t('common.loading')}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="rounded" height={160} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (state.status === 'error') {
    return (
      <ApiErrorState what={t('errors.what.subscriber')} error={state.error} onRetry={state.reload} />
    );
  }

  const sub = state.data?.subscription;
  if (!sub) return null;
  const derived = deriveStatus(sub, new Date());
  const offer = offerById(offers, sub.retailOfferId);
  const interval =
    sub.interval === 'YEAR' || sub.interval === 'MONTH'
      ? enumLabel(t, 'pricing.interval', sub.interval)
      : unavailable;
  const card =
    sub.cardBrand || sub.maskedCard
      ? [sub.cardBrand, sub.maskedCard].filter(Boolean).join(' ')
      : unavailable;
  const pending = sub.pendingChange;
  const pendingOn = formatCivilOrDash(pending?.effectiveOn, locale, unavailable);
  const pendingInterval =
    pending?.interval === 'YEAR' || pending?.interval === 'MONTH'
      ? enumLabel(t, 'pricing.interval', pending.interval)
      : unavailable;
  const promotion = sub.appliedPromotion
    ? promotionSummary(sub.appliedPromotion, { t, locale, currency, offers })
    : null;

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3" component="h2">
            {sub.email ?? unavailable}
          </Typography>
          {sub.displayName ? (
            <Typography variant="body2" sx={{ color: tokens.text2 }}>
              {sub.displayName}
            </Typography>
          ) : null}
          <Box sx={{ mt: 1 }}>
            <StatusChip domain="subscriber" status={derived} />
          </Box>
        </Box>
        <Button onClick={onClose}>{t('subscribers.closeDetail')}</Button>
      </Stack>
      <Typography variant="body2" sx={{ mt: 2, color: tokens.text2 }}>
        {t('subscribers.facts.line', {
          offer: offerLabel(
            offer,
            currency,
            locale,
            offerPeriodLabel(
              offer?.interval ?? sub.interval,
              t('dashboard.perMonth'),
              t('dashboard.perYear'),
            ),
            unavailable,
          ),
          interval,
          card,
          trial: formatCivilOrDash(sub.trialEndsOn, locale, unavailable),
          since: formatCivilOrDash(sub.createdAt, locale, unavailable),
          renews: formatCivilOrDash(sub.nextBillingOn, locale, unavailable),
          comped: formatCivilOrDash(sub.compedUntil, locale, unavailable),
        })}
      </Typography>
      {pending ? (
        <Typography variant="body2" sx={{ mt: 1, color: tokens.text2 }}>
          {t('subscribers.pendingChange', {
            interval: pendingInterval,
            date: pendingOn,
          })}
        </Typography>
      ) : null}
      {promotion ? (
        <Typography variant="body2" sx={{ mt: 1, color: tokens.text2 }}>
          {promotion.before}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {promotion.code}
          </Box>
          {promotion.after}
        </Typography>
      ) : null}
      <Box sx={{ mt: 2 }}>
        <StatementsTable statements={state.data?.statements ?? []} />
      </Box>
      <SubscriberSeats sponsorUserId={sub.userId} />
      <SubscriberActions
        detail={state.data ?? { subscription: sub, statements: [], entitlements: [] }}
        offers={offers}
        promotions={promotions}
        offersLoad={offersLoad}
        onRefreshOffers={onRefreshOffers}
        promotionsLoad={promotionsLoad}
        onDone={onDone}
      />
    </Box>
  );
}
